// src/analysis/aps/apsProcess.jsx
import axios from 'axios';
import pako from 'pako';
import { toast } from 'react-toastify';
import { API_BASE_URL } from "../../utils/config";
import { CANAL_JOB, lanzarAnalisisEnSegundoPlano, tituloAnalisis } from "../../utils/analisisJobs";

/*
 * Headers EXACTOS para el flujo APS manual.
 * - EQUIPO no se captura en el modal: se toma del selector y se guarda con su nombre
 *   canónico (Dron/Helicóptero/Avioneta), el mismo valor que muestra y filtra el dashboard.
 * - La plantilla CSV/Excel ya no se utiliza para Aplicaciones Aéreas.
 */
const CSV_HEADERS = [
    'RESPONSABLE',
    'NOMBRE DE OPERADOR',
    'FECHA INICIO',
    'FECHA FINAL',
    'HORA INICIO',
    'HORA FIN',
    'CODIGO DE MAQUINA',
    'EFICIENCIA',
    'DOSIS TEORICA',
    'VOLUMEN DE AGUA',
    'DOSIS DE PRODUCTO',
    'FORMULACION',
    'TIPO_PRODUCTO',
    'PRODUCTO',
    'HUMEDAD DEL CULTIVO',
    'TCH ESTIMADO',
    'EQUIPO'
];

/* Mapeo de headers a llaves internas del modal */
const KEY_MAP = {
    'RESPONSABLE': 'responsable',
    'NOMBRE DE OPERADOR': 'nombreOperador',
    'FECHA INICIO': 'fechaInicio',
    'FECHA FINAL': 'fechaFinal',
    'HORA INICIO': 'horaInicio',
    'HORA FIN': 'horaFin',
    'CODIGO DE MAQUINA': 'codigoMaquina',
    'EFICIENCIA': 'eficiencia',
    'DOSIS TEORICA': 'dosisTeorica',
    // ✅ IMPORTANTÍSIMO:
    // En el modal los campos nuevos se guardan como:
    //   - volumenAguaL
    //   - dosisProductoLHa
    // Si aquí no se usan esas llaves, el CSV manual sale con esas columnas vacías.
    'VOLUMEN DE AGUA': 'volumenAguaL',
    'DOSIS DE PRODUCTO': 'dosisProductoLHa',
    'FORMULACION': 'formulacion',
    'TIPO_PRODUCTO': 'tipoProducto',
    'PRODUCTO': 'producto',
    'HUMEDAD DEL CULTIVO': 'humedadCultivo',
    'TCH ESTIMADO': 'tchEstimado'
};

// Fecha del input type="date" (YYYY-MM-DD) -> DD/MM/YYYY (formato esperado por el backend)
const toDDMMYYYY = (v) => {
    const s = (v ?? '').toString().trim();
    const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
    if (!m) return s;
    const [, yyyy, mm, dd] = m;
    return `${dd}/${mm}/${yyyy}`;
};

// Hora del input type="time" (HH:MM) -> HH:MM:SS (más robusto para parsers)
const toHHMMSS = (v) => {
    const s = (v ?? '').toString().trim();
    if (!s) return '';
    // ya trae segundos
    if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
    // HH:MM
    if (/^\d{2}:\d{2}$/.test(s)) return `${s}:00`;
    return s;
};

/* Escapar campo CSV */
const esc = (v) => {
    const s = (v ?? '').toString();
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
};

// Nombre canónico de EQUIPO persistido en Mongo (selector DRONE → "Dron").
const platformLabel = (value) => {
    const v = String(value || '').trim().toUpperCase();
    if (v === 'HELICOPTERO' || v === 'HELICOPTER') return 'Helicóptero';
    if (v === 'AVIONETA' || v === 'AIRPLANE') return 'Avioneta';
    return 'Dron';
};

/* Construir CSV desde filas del modal (array de objetos con llaves internas) */
const buildCsvFromManualRows = (rows, materialType = 'LIQUIDO', equipo = 'Dron') => {
    const header = CSV_HEADERS.join(',');
    const lines = rows.map(r => CSV_HEADERS.map(h => {
        const key = KEY_MAP[h];

        // Compat: si el usuario tenía rows guardadas con llaves antiguas,
        // no queremos perder esos valores al reconstruir CSV.
        let value = r?.[key];

        if ((h === 'VOLUMEN DE AGUA') && (value === undefined || value === null || value === '')) {
            value = r?.volumenAgua ?? r?.volumenAguaL;
        }
        if ((h === 'DOSIS DE PRODUCTO') && (value === undefined || value === null || value === '')) {
            value = r?.dosisProducto ?? r?.dosisProductoLHa;
        }

        // Guardar explícitamente si la aplicación fue líquida o sólida para dashboard de indicadores.
        if (h === 'FORMULACION' || h === 'TIPO_PRODUCTO') {
            value = materialType || 'LIQUIDO';
        }
        if (h === 'EQUIPO') {
            value = equipo;
        }

        // Normalizar formatos para que el backend no falle al calcular TIEMPO_TOTAL u otros derivados.
        if (h === 'FECHA INICIO' || h === 'FECHA FINAL') {
            value = toDDMMYYYY(value);
        }
        if (h === 'HORA INICIO' || h === 'HORA FIN') {
            value = toHHMMSS(value);
        }

        return esc(value);
    }).join(','));
    return [header, ...lines].join('\n');
};

export const ejecutarProcesoAps = async ({
                                             selectedZipFile,
                                             idMax,
                                             idUsuario,
                                             setProcessingFinished,
                                             socket,
                                             socketSessionID,
                                             activarEdicionInteractiva,
                                             setShowProgressBar,
                                             setProgress,
                                             setTitleLoader,
                                             materialType,
                                             machineryType,
                                             applicationMode = 'CONVENCIONAL',
                                             targetPolygonFile = null,
                                             lotePolygonsGeojson = null,
                                             uiFiltros = {},
                                         }) => {
    setProgress(0);
    setTitleLoader("Iniciando mapeo de aplicaciones aéreas...");
    setShowProgressBar(true);

    try {
        // 1) Fuente de datos: únicamente filas manuales guardadas desde AplicacionesAereasModal
        const manualRows = (() => {
            try {
                const raw = localStorage.getItem('aplicacionesAereas_manual_rows') || '[]';
                const arr = JSON.parse(raw);
                return Array.isArray(arr) ? arr : [];
            } catch {
                return [];
            }
        })();

        const useManual = manualRows.length > 0;

        if (!useManual) {
            throw new Error('Debes ingresar y guardar los datos manuales de Aplicaciones Aéreas antes de ejecutar el análisis.');
        }

        // 2) Asegurar un ID_ANALISIS para amarrar datos manuales
        let idAnalisisToSend = idMax;
        if (!idAnalisisToSend) {
            const fromLS = localStorage.getItem('aplicacionesAereas_manual_id');
            if (fromLS) {
                idAnalisisToSend = fromLS;
            } else {
                idAnalisisToSend = String(Date.now()); // fallback local
                localStorage.setItem('aplicacionesAereas_manual_id', idAnalisisToSend);
            }
        }

        const isVar = String(applicationMode || '').toUpperCase() === 'VAR';
        if (isVar && (!selectedZipFile || !targetPolygonFile)) {
            throw new Error('Para VRA debes subir el archivo de aplicación (puntos SHP/ZIP, franjas KML/KMZ/ZIP o el .log del controlador) y los polígonos de maleza.');
        }

        // Datos manuales → CSV comprimido (mismo formato de siempre).
        const csv = buildCsvFromManualRows(manualRows, materialType, platformLabel(machineryType));
        const gzBlob = new Blob([pako.gzip(csv)], { type: 'application/gzip' });

        // El análisis corre en el servidor (segundo plano): primero los datos
        // manuales y luego el mapeo, los mismos pasos que hacía el navegador.
        const pasos = [{
            ruta: '/mongo/insert_csv',
            campos: { collection_name: 'aplicaciones_aereas', session_id: CANAL_JOB, id_analisis: String(idAnalisisToSend) },
            archivos: { file: { blob: gzBlob, nombre: 'aplicaciones_aereas_manual.csv.gz' } },
        }];

        if (isVar) {
            // Cualquier equipo (dron, helicóptero, avioneta) y cualquiera de los
            // dos archivos: puntos SHP/ZIP o franjas KML/KMZ/ZIP. El servicio GIS
            // detecta cuál llegó y lo mapea con las unidades del equipo.
            // El lote de la plataforma es distinto al ZIP objetivo de maleza:
            // se envía como GeoJSON para que el GIS calcule AREA_LOTE_HA.
            const loteFeatures = Array.isArray(lotePolygonsGeojson?.features)
                ? lotePolygonsGeojson.features.filter((feature) => feature?.geometry)
                : [];
            const loteBlob = loteFeatures.length > 0
                ? new Blob([JSON.stringify({ type: 'FeatureCollection', features: loteFeatures })], { type: 'application/geo+json' })
                : null;
            pasos.push({
                ruta: '/mapping/tasa_variable',
                campos: {
                    id_analisis: String(idAnalisisToSend),
                    session_id: CANAL_JOB,
                    equipo: platformLabel(machineryType),
                    formulation: materialType || 'LIQUIDO',
                    incluir_puntos: 'true',
                },
                archivos: {
                    puntos_aplicacion: selectedZipFile,
                    poligonos_objetivo: targetPolygonFile,
                    poligono_lote: loteBlob ? { blob: loteBlob, nombre: 'lote_plataforma.geojson' } : null,
                },
            });
        } else {
            pasos.push({
                ruta: '/mapping/aplicaciones_areas',
                campos: {
                    filtrar: activarEdicionInteractiva ? 'true' : 'false',
                    session_id: CANAL_JOB,
                    formulation: materialType || 'LIQUIDO',
                    platform: machineryType || 'DRONE',
                    id_analisis: String(idAnalisisToSend),
                },
                archivos: { polygon_path: selectedZipFile || null },
            });
        }

        setProgress(5);
        setTitleLoader('Enviando archivos al servidor...');

        // Créditos por producto: el backend valida el saldo antes de encolar
        // (402 si no alcanza) y descuenta al terminar bien.
        const respuesta = await lanzarAnalisisEnSegundoPlano({
            tipoAnalisis: 'APLICACIONES_AEREAS',
            modo: isVar ? 'VRA' : 'NORMAL',
            equipo: machineryType || 'DRONE',
            titulo: tituloAnalisis('APLICACIONES_AEREAS', { equipo: machineryType || 'DRONE', modo: isVar ? 'VRA' : 'NORMAL' }),
            idUsuario,
            idAnalisis: idAnalisisToSend,
            sesionOrigen: socketSessionID,
            pasos,
            ui: uiFiltros,
        });

        setProgress(10);
        setTitleLoader(isVar
            ? 'VRA en procesamiento en el servidor. El mapa se actualizará al finalizar...'
            : 'Análisis en procesamiento en el servidor. El mapa se actualizará al finalizar...');
        toast.info(respuesta?.message || 'El análisis se está procesando en segundo plano.', {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 7000
        });
    } catch (error) {
        console.error("Error al iniciar el proceso de Aplicaciones Aéreas:", error);
        const errorMessage = error?.response?.data?.message || error.message || 'Ocurrió un error, consulta con tu administrador.';
        toast.error(errorMessage, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 5000
        });
        setShowProgressBar(false);
    }
};

export const ejecutarProcesoSinArchivoAps = async ({
                                                       idMax,
                                                       idUsuario,
                                                       setProcessingFinished,
                                                       setShowProgressBar
                                                   }) => {
    const formData = new FormData();
    formData.append('esPrimeraIteracion', 'true');
    formData.append('esKmlInteractivo', 'true');

    setShowProgressBar(true);

    try {
        const response = await axios.post(`${API_BASE_URL}dashboard/execBash/${idUsuario}/1/${idMax}/0/ok/0`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response?.data?.status === "error") {
            throw new Error(response?.data?.message || 'Error ejecutando proceso');
        }

        setProcessingFinished(true);
        toast.success('Proceso ejecutado exitosamente.', { position: toast.POSITION.TOP_RIGHT, autoClose: 5000 });
        setShowProgressBar(false);
    } catch (error) {
        console.error("Error al procesar el lote de Aplicaciones Aéreas sin archivo:", error);
        const errorMessage = error.message || 'Ocurrió un error, consulta con tu administrador.';
        toast.error(errorMessage, { position: toast.POSITION.TOP_RIGHT, autoClose: 5000 });
        setShowProgressBar(false);
    }
};
